import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAssignments, AssignmentRow } from 'services/report';
import { usePaging } from 'hooks/usePaging';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { CriticalityLevel, DEFAULT_CRITICALITY_LEVELS } from 'utils/issueCriticality';
import { exportAssignmentsCsv } from '../utils/issuesListExport';
import { useRoomContext } from 'contexts/RoomContext';

const ROWS_PER_PAGE = 30;

export type { AssignmentRow };

export type AssignmentsApiFilters = {
  issueType?: 'Room' | 'Asset' | 'AdHoc';
  clientName?: string;
  locationId?: number;
  status?: string;
  assignedToUserId?: number;
  criticalityKey?: string;
  orderBy?: string;
  order?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
  keyword?: string;
};

export const useIssuesAssignmentsTab = () => {
  const [assignmentsRaw, setAssignmentsRaw] = useState<AssignmentRow[]>([]);
  const [apiFilters, setApiFiltersState] = useState<AssignmentsApiFilters>({});
  const [exporting, setExporting] = useState(false);
  // Local page state — single source of truth to avoid race conditions with usePaging
  const [page, setPage] = useState(1);
  const { users } = useRoomContext();

  const { pagingInfo, setCurrentPage, setTotal } = usePaging({
    rowsPerPage: ROWS_PER_PAGE
  });

  // Sync local page → usePaging so the pagination UI renders correctly
  useEffect(() => {
    setCurrentPage(page);
  }, [page, setCurrentPage]);

  const fetchAssignments = useCallback(
    async (params: { page: number; size: number } & AssignmentsApiFilters) => {
      const data = await getAssignments(params);
      setAssignmentsRaw(data.assignments ?? []);
      setTotal(data.total ?? 0);
      return data;
    },
    [setTotal]
  );

  const { asyncCallback: asyncFetchAssignments, isLoading: loading } = useAsyncCallback(
    fetchAssignments,
    [fetchAssignments]
  );

  // Single fetch trigger: fires whenever page OR apiFilters change
  useEffect(() => {
    asyncFetchAssignments({
      page,
      size: ROWS_PER_PAGE,
      ...apiFilters
    });
  }, [page, apiFilters, asyncFetchAssignments]);

  const assigneeOptions = useMemo(() => {
    if (!users || users.length === 0) return [];
    return users.map((u) => ({
      label: u.label,
      value: Number(u.value)
    }));
  }, [users]);

  const statusOptions = useMemo(() => {
    const statuses = Array.from(
      new Set(assignmentsRaw.map((item) => item.status).filter((value) => !!value))
    );
    return statuses.map((status) => ({ label: status, value: status }));
  }, [assignmentsRaw]);

  const issueTypeOptions = useMemo(() => {
    const types = Array.from(new Set(assignmentsRaw.map((item) => item.issueType).filter(Boolean)));
    return types.map((t) => ({ label: t === 'Room' ? 'Location' : t, value: t }));
  }, [assignmentsRaw]);

  const clientOptions = useMemo(() => {
    const names = Array.from(
      new Set(assignmentsRaw.map((item) => item.clientName).filter(Boolean))
    );
    return names.map((n) => ({ label: n, value: n }));
  }, [assignmentsRaw]);

  const setApiFilter = useCallback(
    (key: keyof AssignmentsApiFilters, value: string | number | undefined) => {
      setApiFiltersState((prev) => {
        const next =
          value === undefined || value === '' || value === null
            ? { ...prev, [key]: undefined }
            : { ...prev, [key]: value as AssignmentsApiFilters[typeof key] };
        if (next.issueType === undefined) delete next.issueType;
        if (next.clientName === undefined) delete next.clientName;
        if (next.locationId === undefined) delete next.locationId;
        if (next.status === undefined) delete next.status;
        if (next.assignedToUserId === undefined) delete next.assignedToUserId;
        if (next.orderBy === undefined) delete next.orderBy;
        if (next.order === undefined) delete next.order;
        if (next.dateFrom === undefined) delete next.dateFrom;
        if (next.dateTo === undefined) delete next.dateTo;
        if (next.criticalityKey === undefined) delete next.criticalityKey;
        if (next.keyword === undefined) delete next.keyword;
        return next;
      });
      // Always reset to page 1 when a filter changes
      setPage(1);
    },
    []
  );

  const issueTypeFilter = apiFilters.issueType ?? '';
  const clientNameFilter = apiFilters.clientName ?? '';
  const assignedToFilter = apiFilters.assignedToUserId ?? '';
  const statusFilter = apiFilters.status ?? '';
  const criticalityFilter = apiFilters.criticalityKey ?? '';
  const keywordFilter = apiFilters.keyword ?? '';

  const clearFilters = useCallback(() => {
    setApiFiltersState({});
    setPage(1);
  }, []);

  const exportCsv = useCallback(
    async (criticalityLevels: CriticalityLevel[] = DEFAULT_CRITICALITY_LEVELS) => {
      setExporting(true);
      try {
        await exportAssignmentsCsv(apiFilters, criticalityLevels);
      } finally {
        setExporting(false);
      }
    },
    [apiFilters]
  );

  const refreshAssignments = useCallback(() => {
    asyncFetchAssignments({
      page,
      size: ROWS_PER_PAGE,
      ...apiFilters
    });
  }, [page, apiFilters, asyncFetchAssignments]);

  return {
    assignments: assignmentsRaw,
    loading,
    assigneeOptions,
    statusOptions,
    issueTypeOptions,
    clientOptions,
    apiFilters,
    issueTypeFilter,
    setIssueTypeFilter: (value: string) =>
      setApiFilter('issueType', value === '' ? undefined : (value as 'Room' | 'Asset' | 'AdHoc')),
    clientNameFilter,
    setClientNameFilter: (value: string) =>
      setApiFilter('clientName', value === '' ? undefined : value),
    assignedToFilter,
    setAssignedToFilter: (value: number | string) =>
      setApiFilter('assignedToUserId', value === '' ? undefined : Number(value)),
    statusFilter,
    setStatusFilter: (value: string) => setApiFilter('status', value === '' ? undefined : value),
    criticalityFilter,
    setCriticalityFilter: (value: string) =>
      setApiFilter('criticalityKey', value === '' ? undefined : value),
    keywordFilter,
    setKeywordFilter: (value: string) => setApiFilter('keyword', value === '' ? undefined : value),
    clearFilters,
    setApiFilter,
    pagingInfo,
    handleChangePage: (newPage: number) => setPage(newPage),
    refreshAssignments,
    exportCsv,
    exporting
  };
};
